import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslate, useShallowEqualSelector } from '@hooks';
import { importStat, fetchUploadCdnUrl } from '@actions';
import { uploadStatuses } from '@dotone/react-ui-core/constants';
import { ChevronsDown as ProceedIcon } from 'react-feather';
import { TablePaginated, ButtonFlat, Button, UploadSummaryModal } from '@dotone/react-ui-core';
import { formatDateTime, formatFileStatus, formatTextareaClipboard } from '../formatters';

const formatStatus = (cell, row, { onProceedClick, t }) => {
  return (
    <>
      {formatFileStatus(cell)}
      {row.status === uploadStatuses.PENDING && row.summaryRequired && row.summary && (
        <Button
          size="sm"
          color="primary"
          onClick={() => onProceedClick(row)}
          className="btn-block mt-2"
        >
          <ProceedIcon size={12} className="mr-1" />
          {t('common:actions.proceed')}
        </Button>
      )}
    </>
  );
};

const formatActions = (cell, row, { onDownload }) => (
  <ButtonFlat
    variant="download"
    disabled={!row.cdnUrl}
    iconProps={{ size: 18 }}
    className="mr-2"
    onClick={() => onDownload(row)}
  />
);

const getColumns = ({ t, onDownload, onProceedClick }) => [
  {
    dataField: 'id',
    text: t('id'),
  },
  {
    dataField: 'descriptions',
    text: t('descriptions'),
  },
  {
    dataField: 'createdAt',
    text: t('createdAt'),
    formatter: formatDateTime,
  },
  {
    dataField: 'status',
    text: t('status'),
    formatter: (cell, row) => formatStatus(cell, row, { onProceedClick, t }),
  },
  {
    dataField: 'summary',
    text: t('summary'),
    formatter: (cell) => cell && formatTextareaClipboard(t('summaryPlain', cell)),
  },
  {
    dataField: 'errorDetails',
    text: t('errorDetails'),
    headerStyle: { width: '20%' },
    formatter: (cell) => formatTextareaClipboard(cell),
  },
  {
    dataField: 'uploadedBy',
    text: t('uploadedBy'),
  },
  {
    dataField: 'actions',
    text: t('file'),
    classes: 'align-middle text-center',
    isDummyField: true,
    formatter: (cell, row) => formatActions(cell, row, { onDownload }),
  },
];

const UploadTable = (props) => {
  const { t } = useTranslate('upload', 'model');
  const dispatch = useDispatch();
  const [current, setCurrent] = useState(false);
  const { isLoading, status } = useShallowEqualSelector(({ statImport }) => statImport);

  const handleClick = (row) => {
    setCurrent(row);
  };

  const handleToggle = () => {
    setCurrent(null);
  };

  const handleDownload = async (row) => {
    const cdnUrl = await fetchUploadCdnUrl(row.id);

    if (cdnUrl) window.open(cdnUrl, '_blank');
  };

  const handleImport = () => {
    dispatch(importStat({ uploadId: current.id }));
  };

  useEffect(() => {
    if (status === 'success' && !isLoading) {
      setCurrent(null);
    }
  }, [setCurrent, status, isLoading]);

  const columns = getColumns({
    t,
    onDownload: handleDownload,
    onProceedClick: handleClick,
  });

  const tableProps = {
    remote: true,
    searchable: false,
    responsive: true,
    condensed: true,
    hover: true,
    classes: 'text-sm stylish-table',
    keyField: 'id',
    columns,
    ...props,
  };

  return (
    <>
      <TablePaginated {...tableProps} />
      <UploadSummaryModal
        isOpen={!!current}
        upload={current || {}}
        onToggle={handleToggle}
        onSubmit={handleImport}
        submitting={isLoading}
      />
    </>
  );
};

export default UploadTable;
